import { authenticatedCopilotServer } from "@/core/api";
import { logger } from "@/core/logger";
import { getPaginationHeaders } from "@/core/api/utils/getPaginationHeaders";
import {
  AddCourtCredentialPayload,
  CourtCredentialPayload,
  CourtCredentialType,
  GetLegalProceedingsPayload,
  ImportLegalProceedingFromOABPayload,
  LegalProceedingHistory,
  LegalProceedingRequest,
  LegalProceedingToCasePayload,
  LegalProceedingType,
  MonitoredCountResponse,
  MonitoringStatusResponse,
  MonitoringStatusesResponse,
  OABType,
  UntrackLegalProceedingPayload,
} from "./types";
import { PaginationPayload } from "@/core/api/types";
import {
  legalProceedingFromPaginationHeaders,
  responseFromPaginationHeaders,
} from "@/core/api/utils/fromPaginationHeaders";

export const LegalProceedingService = {
  getLegalProceedings: async ({ pagination }: GetLegalProceedingsPayload) => {
    try {
      const response = await authenticatedCopilotServer.get<LegalProceedingRequest>(`/legalProceedings`, {
        headers: getPaginationHeaders(pagination),
      });

      return legalProceedingFromPaginationHeaders(response);
    } catch (e) {
      logger.error("getLegalProceedings", { error: e });
      throw e;
    }
  },

  importLegalProceedingFromOAB: async (payload: ImportLegalProceedingFromOABPayload) => {
    try {
      const response = await authenticatedCopilotServer.post<LegalProceedingRequest>(
        `/users/${payload.userId}/oab`,
        payload.oabs
      );
      return response.data;
    } catch (e) {
      logger.error("importLegalProceedingFromOAB", { error: e });
      throw e;
    }
  },

  attachLegalProceedingToCase: async ({ cnj, caseId }: { cnj: string; caseId?: string }) => {
    try {
      const response = await authenticatedCopilotServer.put(`/cases/${caseId}/legalProceedings`, {
        cnj,
      });
      return response.data;
    } catch (e) {
      logger.error("attachLegalProceedingToCase", { error: e });
      throw e;
    }
  },

  transformLegalProceedingsToCase: async (payload: LegalProceedingToCasePayload) => {
    try {
      const response = await authenticatedCopilotServer.post(`/cases/cnj`, payload);
      return response.data;
    } catch (e) {
      logger.error("transformLegalProceedingsToCase", { error: e });
      throw e;
    }
  },

  untrackLegalProceeding: async (payload: UntrackLegalProceedingPayload) => {
    try {
      const response = await authenticatedCopilotServer.delete(`/legalProceedings/${payload.cnj}/track`);
      return response.data;
    } catch (e) {
      logger.error("untrackLegalProceeding", { error: e });
      throw e;
    }
  },

  reactivateMonitoring: async () => {
    try {
      const response = await authenticatedCopilotServer.post(`/legalProceedings/reactivate`, {});
      return response.data;
    } catch (e) {
      logger.error("reactivateMonitoring", { error: e });
      throw e;
    }
  },

  getMonitoredCount: async (): Promise<MonitoredCountResponse> => {
    try {
      const response = await authenticatedCopilotServer.get<MonitoredCountResponse>(
        `/legalProceedings/monitored/count`
      );
      return response.data;
    } catch (e) {
      logger.error("getMonitoredCount", { error: e });
      throw e;
    }
  },

  getMonitoringStatuses: async (): Promise<MonitoringStatusesResponse> => {
    try {
      const response = await authenticatedCopilotServer.get<MonitoringStatusesResponse>(
        `/legalProceedings/monitored/statuses`
      );
      return response.data;
    } catch (e) {
      logger.error("getMonitoringStatuses", { error: e });
      throw e;
    }
  },

  getLegalProceedingsToCase: async () => {
    try {
      const response = await authenticatedCopilotServer.get(`/legalProceedings/toCase`);
      return response.data;
    } catch (e) {
      logger.error("getLegalProceedingsToCase", { error: e });
      throw e;
    }
  },

  getCourtCredentials: async (userId: string) => {
    try {
      const response = await authenticatedCopilotServer.get<CourtCredentialType[]>(`/users/${userId}/courtCredentials`);
      return response.data;
    } catch (e) {
      logger.error("getCourtCredentials", { error: e });
      throw e;
    }
  },

  addCourtCredentials: async (userId: string, payload: AddCourtCredentialPayload) => {
    try {
      const response = await authenticatedCopilotServer.post(`/users/${userId}/courtCredentials`, payload);
      return response.data;
    } catch (e) {
      logger.error("addCourtCredentials", { error: e });
      throw e;
    }
  },

  revokeCourtCredentials: async (userId: string, id: string) => {
    try {
      const response = await authenticatedCopilotServer.delete(`/users/${userId}/courtCredentials/${id}`);
      return response.data;
    } catch (e) {
      logger.error("revokeCourtCredentials", { error: e });
      throw e;
    }
  },

  updateCourtCredentials: async (userId: string, id: string, payload: CourtCredentialPayload) => {
    try {
      const response = await authenticatedCopilotServer.put(`/users/${userId}/courtCredentials/${id}`, payload);
      return response.data;
    } catch (e) {
      logger.error("updateCourtCredentials", { error: e });
      throw e;
    }
  },

  listOabsByUserId: async (userId: string, pagination?: PaginationPayload) => {
    try {
      const response = await authenticatedCopilotServer.get(`/users/${userId}/oab`, {
        headers: getPaginationHeaders(pagination),
      });

      return responseFromPaginationHeaders<OABType>(response);
    } catch (e) {
      logger.error("listOabsByUserId", { error: e });
      throw e;
    }
  },

  deleteOab: async (userId: string, oabId: string) => {
    try {
      const response = await authenticatedCopilotServer.delete(`/users/${userId}/oab/${oabId}`);
      return response.data;
    } catch (e) {
      logger.error("deleteOab", { error: e });
      throw e;
    }
  },

  getLegalProceedingsAttachedInCase: async (caseId: string) => {
    try {
      const response = await authenticatedCopilotServer.get<{
        legalProceedings: LegalProceedingType[];
      }>(`/cases/${caseId}/legalProceedings`);
      return response.data;
    } catch (e) {
      logger.error("getLegalProceedingsAttachedInCase", { error: e });
      throw e;
    }
  },

  getLegalProceedingHistory: async (cnj: string, instance: string) => {
    try {
      const response = await authenticatedCopilotServer.get<LegalProceedingHistory>(
        `/legalProceedings/${cnj}/${instance}/history`
      );
      return response.data;
    } catch (e) {
      logger.error("getLegalProceedingHistory", { error: e });
      throw e;
    }
  },

  clearLegalProceedings: async ({ userId, companyId }: { userId: string; companyId: string }) => {
    try {
      const response = await authenticatedCopilotServer.post(`/admin/legal-proceedings/clear`, {
        userId,
        companyId,
      });
      return response.data;
    } catch (e) {
      logger.error("clearLegalProceedings", { error: e });
      throw e;
    }
  },

  clearJuditTrackings: async () => {
    try {
      const response = await authenticatedCopilotServer.delete<{ message: string }>(`/admin/judit/trackings`);
      return response.data;
    } catch (e) {
      logger.error("clearJuditTrackings", { error: e });
      throw e;
    }
  },

  getMonitoringStatus: async () => {
    try {
      const response = await authenticatedCopilotServer.get<MonitoringStatusResponse>(`/monitoring/status`);
      return response.data;
    } catch (e) {
      logger.error("getMonitoringStatus", { error: e });
      throw e;
    }
  },
};
